import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useCanvas, drawGrid, drawWarehouseOutline, clearCanvas } from '../../hooks/useCanvas';
import { useTheme } from '../../contexts/ThemeContext';
import type {
  ViewportState,
  Position,
  Warehouse,
  WarehouseZone,
  WarehouseRack,
  WarehouseBin,
  MapEditorTool,
  BoundingBox,
} from '../../types/warehouse';
import { MIN_ZOOM, MAX_ZOOM } from '../../types/warehouse';
import { snapToGrid, doBoxesOverlap, getZoneBoundingBox, getRackBoundingBox } from '../../utils/warehouseUtils';

export type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w';

export interface WarehouseMapCanvasProps {
  warehouse: Warehouse;
  zones: WarehouseZone[];
  racks: WarehouseRack[];
  bins?: WarehouseBin[];
  selectedZoneId: string | null;
  selectedRackId: string | null;
  activeTool: MapEditorTool;
  showGrid: boolean;
  gridSize: number;
  showZoneLabels: boolean;
  showRackLabels: boolean;
  showBinCounts?: boolean;
  showRulers?: boolean;
  zoneOpacity: number;
  snapToGridEnabled?: boolean;
  showCollisionWarnings?: boolean;
  viewport: ViewportState;
  onViewportChange?: (viewport: ViewportState) => void;
  onZoneSelect?: (zoneId: string | null) => void;
  onRackSelect?: (rackId: string | null) => void;
  onZoneMove?: (zoneId: string, position: Position) => void;
  onZoneResize?: (zoneId: string, width: number, height: number, position?: Position) => void;
  onRackMove?: (rackId: string, position: Position) => void;
  onCanvasClick?: (worldPosition: Position) => void;
  onZoneCreate?: (position: Position) => void;
  onRackCreate?: (position: Position) => void;
}

interface DragState {
  type: 'pan' | 'move-zone' | 'move-rack' | 'resize-zone' | 'create-zone' | 'create-rack' | null;
  startScreen: Position;
  startWorld: Position;
  startViewport: ViewportState;
  targetId: string | null;
  initialPosition?: Position;
  initialSize?: { width: number; height: number };
  resizeHandle?: ResizeHandle;
  createStart?: Position;
}

const SCALE_FACTOR = 3;
const RULER_SIZE = 24;
const MIN_ZONE_SIZE = 5;

export const WarehouseMapCanvas: React.FC<WarehouseMapCanvasProps> = ({
  warehouse,
  zones,
  racks,
  bins = [],
  selectedZoneId,
  selectedRackId,
  activeTool,
  showGrid,
  gridSize,
  showZoneLabels,
  showRackLabels,
  showBinCounts = false,
  showRulers = true,
  zoneOpacity,
  snapToGridEnabled = true,
  showCollisionWarnings = true,
  viewport,
  onViewportChange,
  onZoneSelect,
  onRackSelect,
  onZoneMove,
  onZoneResize,
  onRackMove,
  onCanvasClick,
  onZoneCreate,
  onRackCreate,
}) => {
  const { isDark } = useTheme();
  const isDarkMode = isDark;

  const rulerOffset = showRulers ? RULER_SIZE : 0;

  const {
    canvasRef,
    containerRef,
    canvasSize,
    zoomIn,
    zoomOut,
    resetView,
    screenToWorld,
    fitToContent,
  } = useCanvas({
    viewport,
    rulerOffset,
    onViewportChange,
  });

  const viewportRef = useRef<ViewportState>(viewport);
  useEffect(() => {
    viewportRef.current = viewport;
  }, [viewport]);

  const [dragState, setDragState] = useState<DragState>({
    type: null,
    startScreen: { x: 0, y: 0 },
    startWorld: { x: 0, y: 0 },
    startViewport: { x: 0, y: 0, scale: 1 },
    targetId: null,
  });

  const [hoveredZoneId, setHoveredZoneId] = useState<string | null>(null);
  const [hoveredRackId, setHoveredRackId] = useState<string | null>(null);
  const [hoveredHandle, setHoveredHandle] = useState<ResizeHandle | null>(null);
  const [cursor, setCursor] = useState<string>('default');
  const [collisionZones, setCollisionZones] = useState<string[]>([]);
  const [mousePosition, setMousePosition] = useState<Position | null>(null);

  const animationFrameRef = useRef<number | null>(null);

  const warehouseWidth = warehouse.length * SCALE_FACTOR;
  const warehouseHeight = warehouse.width * SCALE_FACTOR;

  const snapPositionToGrid = useCallback(
    (pos: Position): Position => {
      if (!snapToGridEnabled) return pos;
      return {
        x: snapToGrid(pos.x, gridSize),
        y: snapToGrid(pos.y, gridSize),
      };
    },
    [snapToGridEnabled, gridSize]
  );

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height } = canvasSize;
    if (width === 0 || height === 0) return;

    clearCanvas(ctx, width, height, isDarkMode);

    drawWarehouseOutline(ctx, viewport, warehouseWidth, warehouseHeight, isDarkMode, rulerOffset, warehouse.border_color || '#2563eb');

    if (showGrid) {
      drawGrid(ctx, viewport, width, height, gridSize, isDarkMode, rulerOffset, warehouseWidth, warehouseHeight);
    }

    drawZones(
      ctx,
      viewport,
      zones,
      selectedZoneId,
      hoveredZoneId,
      zoneOpacity,
      showZoneLabels,
      isDarkMode,
      collisionZones,
      showCollisionWarnings,
      rulerOffset
    );

    drawRacks(
      ctx,
      viewport,
      racks,
      zones,
      bins,
      selectedRackId,
      hoveredRackId,
      showRackLabels,
      showBinCounts,
      isDarkMode,
      showCollisionWarnings,
      rulerOffset
    );

    if (selectedZoneId) {
      const zone = zones.find((z) => z.id === selectedZoneId);
      if (zone) {
        drawSelectionHandles(
          ctx,
          viewport,
          zone.x_position * SCALE_FACTOR,
          zone.y_position * SCALE_FACTOR,
          zone.zone_width * SCALE_FACTOR,
          zone.zone_height * SCALE_FACTOR,
          isDarkMode,
          hoveredHandle,
          rulerOffset
        );
      }
    }

    if (selectedRackId) {
      const rack = racks.find((r) => r.id === selectedRackId);
      if (rack) {
        drawRackSelection(ctx, viewport, rack, isDarkMode, rulerOffset);
      }
    }

    if (dragState.type === 'create-zone' && dragState.createStart) {
      drawZoneCreationPreview(ctx, viewport, dragState.createStart, dragState.startWorld, isDarkMode, rulerOffset);
    }

    if (showRulers) {
      drawRulers(ctx, viewport, width, height, warehouse.unit, isDarkMode);
    }
  }, [
    canvasRef,
    canvasSize,
    viewport,
    isDarkMode,
    showGrid,
    showRulers,
    gridSize,
    warehouseWidth,
    warehouseHeight,
    zones,
    racks,
    bins,
    selectedZoneId,
    selectedRackId,
    hoveredZoneId,
    hoveredRackId,
    hoveredHandle,
    zoneOpacity,
    showZoneLabels,
    showRackLabels,
    showBinCounts,
    collisionZones,
    showCollisionWarnings,
    dragState,
    warehouse.unit,
    rulerOffset,
  ]);

  useEffect(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    animationFrameRef.current = requestAnimationFrame(draw);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [draw]);

  const getMousePosition = useCallback(
    (e: React.MouseEvent | MouseEvent): Position => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };

      const rect = canvas.getBoundingClientRect();
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    },
    [canvasRef]
  );

  const findZoneAtPosition = useCallback(
    (worldPos: Position): WarehouseZone | null => {
      for (let i = zones.length - 1; i >= 0; i--) {
        const zone = zones[i];
        const zoneX = zone.x_position * SCALE_FACTOR;
        const zoneY = zone.y_position * SCALE_FACTOR;
        const zoneWidth = zone.zone_width * SCALE_FACTOR;
        const zoneHeight = zone.zone_height * SCALE_FACTOR;

        if (
          worldPos.x >= zoneX &&
          worldPos.x <= zoneX + zoneWidth &&
          worldPos.y >= zoneY &&
          worldPos.y <= zoneY + zoneHeight
        ) {
          return zone;
        }
      }
      return null;
    },
    [zones]
  );

  const findRackAtPosition = useCallback(
    (worldPos: Position): WarehouseRack | null => {
      for (let i = racks.length - 1; i >= 0; i--) {
        const rack = racks[i];
        const rackX = rack.x_position * SCALE_FACTOR;
        const rackY = rack.y_position * SCALE_FACTOR;
        const rackWidth = rack.rack_length * SCALE_FACTOR;
        const rackHeight = rack.rack_depth * SCALE_FACTOR;

        if (
          worldPos.x >= rackX &&
          worldPos.x <= rackX + rackWidth &&
          worldPos.y >= rackY &&
          worldPos.y <= rackY + rackHeight
        ) {
          return rack;
        }
      }
      return null;
    },
    [racks]
  );

  const findResizeHandleAtPosition = useCallback(
    (screenPos: Position, zone: WarehouseZone): ResizeHandle | null => {
      const currentViewport = viewportRef.current;
      const zoneX = zone.x_position * SCALE_FACTOR * currentViewport.scale + currentViewport.x + rulerOffset;
      const zoneY = zone.y_position * SCALE_FACTOR * currentViewport.scale + currentViewport.y + rulerOffset;
      const zoneW = zone.zone_width * SCALE_FACTOR * currentViewport.scale;
      const zoneH = zone.zone_height * SCALE_FACTOR * currentViewport.scale;

      const handleSize = 10;
      const handles: { handle: ResizeHandle; x: number; y: number }[] = [
        { handle: 'nw', x: zoneX, y: zoneY },
        { handle: 'ne', x: zoneX + zoneW, y: zoneY },
        { handle: 'sw', x: zoneX, y: zoneY + zoneH },
        { handle: 'se', x: zoneX + zoneW, y: zoneY + zoneH },
        { handle: 'n', x: zoneX + zoneW / 2, y: zoneY },
        { handle: 's', x: zoneX + zoneW / 2, y: zoneY + zoneH },
        { handle: 'w', x: zoneX, y: zoneY + zoneH / 2 },
        { handle: 'e', x: zoneX + zoneW, y: zoneY + zoneH / 2 },
      ];

      for (const h of handles) {
        const dx = screenPos.x - h.x;
        const dy = screenPos.y - h.y;
        if (Math.abs(dx) <= handleSize && Math.abs(dy) <= handleSize) {
          return h.handle;
        }
      }

      return null;
    },
    [rulerOffset]
  );

  const checkZoneCollisions = useCallback(
    (box: BoundingBox, excludeId: string): string[] => {
      const collisions: string[] = [];
      for (const zone of zones) {
        if (zone.id === excludeId) continue;
        const zoneBox = getZoneBoundingBox(zone);
        if (doBoxesOverlap(box, zoneBox)) {
          collisions.push(zone.id);
        }
      }
      return collisions;
    },
    [zones]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const screenPos = getMousePosition(e);
      const adjustedScreenPos = { x: screenPos.x - rulerOffset, y: screenPos.y - rulerOffset };
      const worldPos = screenToWorld(adjustedScreenPos.x, adjustedScreenPos.y);
      const currentViewport = viewportRef.current;

      if (activeTool === 'pan' || e.button === 1 || (e.button === 0 && e.shiftKey)) {
        setDragState({
          type: 'pan',
          startScreen: screenPos,
          startWorld: worldPos,
          startViewport: { ...currentViewport },
          targetId: null,
        });
        setCursor('grabbing');
        return;
      }

      if (activeTool === 'zone' && onZoneCreate) {
        setDragState({
          type: 'create-zone',
          startScreen: screenPos,
          startWorld: worldPos,
          startViewport: { ...currentViewport },
          targetId: null,
          createStart: worldPos,
        });
        setCursor('crosshair');
        return;
      }

      if (activeTool === 'rack' && onRackCreate) {
        const snappedPos = snapPositionToGrid({ x: worldPos.x / SCALE_FACTOR, y: worldPos.y / SCALE_FACTOR });
        onRackCreate(snappedPos);
        return;
      }

      if (activeTool === 'select') {
        if (selectedZoneId) {
          const selectedZone = zones.find((z) => z.id === selectedZoneId);
          if (selectedZone) {
            const handle = findResizeHandleAtPosition(screenPos, selectedZone);
            if (handle) {
              setDragState({
                type: 'resize-zone',
                startScreen: screenPos,
                startWorld: worldPos,
                startViewport: { ...currentViewport },
                targetId: selectedZoneId,
                initialPosition: { x: selectedZone.x_position, y: selectedZone.y_position },
                initialSize: { width: selectedZone.zone_width, height: selectedZone.zone_height },
                resizeHandle: handle,
              });
              setCursor(getResizeCursor(handle));
              return;
            }
          }
        }

        const rack = findRackAtPosition(worldPos);
        if (rack) {
          onRackSelect?.(rack.id);
          onZoneSelect?.(null);
          setDragState({
            type: 'move-rack',
            startScreen: screenPos,
            startWorld: worldPos,
            startViewport: { ...currentViewport },
            targetId: rack.id,
            initialPosition: { x: rack.x_position, y: rack.y_position },
          });
          setCursor('move');
          return;
        }

        const zone = findZoneAtPosition(worldPos);
        if (zone) {
          onZoneSelect?.(zone.id);
          onRackSelect?.(null);
          setDragState({
            type: 'move-zone',
            startScreen: screenPos,
            startWorld: worldPos,
            startViewport: { ...currentViewport },
            targetId: zone.id,
            initialPosition: { x: zone.x_position, y: zone.y_position },
            initialSize: { width: zone.zone_width, height: zone.zone_height },
          });
          setCursor('move');
          return;
        }

        onZoneSelect?.(null);
        onRackSelect?.(null);
      }

      onCanvasClick?.(worldPos);
    },
    [
      getMousePosition,
      screenToWorld,
      activeTool,
      selectedZoneId,
      zones,
      findZoneAtPosition,
      findRackAtPosition,
      findResizeHandleAtPosition,
      onZoneSelect,
      onRackSelect,
      onCanvasClick,
      onZoneCreate,
      onRackCreate,
      snapPositionToGrid,
      rulerOffset,
    ]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const screenPos = getMousePosition(e);
      const adjustedScreenPos = { x: screenPos.x - rulerOffset, y: screenPos.y - rulerOffset };
      const currentViewport = viewportRef.current;

      if (dragState.type === 'pan') {
        const dx = screenPos.x - dragState.startScreen.x;
        const dy = screenPos.y - dragState.startScreen.y;

        const newX = dragState.startViewport.x + dx;
        const newY = dragState.startViewport.y + dy;

        onViewportChange?.({ ...currentViewport, x: newX, y: newY });
        return;
      }

      const worldPos = screenToWorld(adjustedScreenPos.x, adjustedScreenPos.y);

      if (dragState.type === 'create-zone' && dragState.createStart) {
        setDragState((prev) => ({
          ...prev,
          startWorld: worldPos,
        }));
        return;
      }

      if (dragState.type === 'resize-zone' && dragState.targetId && dragState.initialPosition && dragState.initialSize && dragState.resizeHandle) {
        const zone = zones.find((z) => z.id === dragState.targetId);
        if (!zone || !onZoneResize) return;

        const dx = (worldPos.x - dragState.startWorld.x) / SCALE_FACTOR;
        const dy = (worldPos.y - dragState.startWorld.y) / SCALE_FACTOR;

        let newX = dragState.initialPosition.x;
        let newY = dragState.initialPosition.y;
        let newWidth = dragState.initialSize.width;
        let newHeight = dragState.initialSize.height;

        const handle = dragState.resizeHandle;

        if (handle.includes('w')) {
          newX = Math.min(dragState.initialPosition.x + dragState.initialSize.width - MIN_ZONE_SIZE, dragState.initialPosition.x + dx);
          newWidth = dragState.initialSize.width - (newX - dragState.initialPosition.x);
        }
        if (handle.includes('e')) {
          newWidth = Math.max(MIN_ZONE_SIZE, dragState.initialSize.width + dx);
        }
        if (handle.includes('n')) {
          newY = Math.min(dragState.initialPosition.y + dragState.initialSize.height - MIN_ZONE_SIZE, dragState.initialPosition.y + dy);
          newHeight = dragState.initialSize.height - (newY - dragState.initialPosition.y);
        }
        if (handle.includes('s')) {
          newHeight = Math.max(MIN_ZONE_SIZE, dragState.initialSize.height + dy);
        }

        if (snapToGridEnabled) {
          newX = snapToGrid(newX, gridSize);
          newY = snapToGrid(newY, gridSize);
          newWidth = snapToGrid(newWidth, gridSize);
          newHeight = snapToGrid(newHeight, gridSize);
        }

        newX = Math.max(0, newX);
        newY = Math.max(0, newY);
        newWidth = Math.max(MIN_ZONE_SIZE, Math.min(newWidth, warehouse.length - newX));
        newHeight = Math.max(MIN_ZONE_SIZE, Math.min(newHeight, warehouse.width - newY));

        if (showCollisionWarnings) {
          const box: BoundingBox = { x: newX, y: newY, width: newWidth, height: newHeight };
          const collisions = checkZoneCollisions(box, dragState.targetId);
          setCollisionZones(collisions);
        }

        onZoneResize(dragState.targetId, newWidth, newHeight, { x: newX, y: newY });
        return;
      }

      if (dragState.type === 'move-zone' && dragState.targetId && dragState.initialPosition) {
        const dx = (worldPos.x - dragState.startWorld.x) / SCALE_FACTOR;
        const dy = (worldPos.y - dragState.startWorld.y) / SCALE_FACTOR;

        let newX = dragState.initialPosition.x + dx;
        let newY = dragState.initialPosition.y + dy;

        if (snapToGridEnabled) {
          newX = snapToGrid(newX, gridSize);
          newY = snapToGrid(newY, gridSize);
        }

        const zone = zones.find((z) => z.id === dragState.targetId);
        if (zone) {
          newX = Math.max(0, Math.min(newX, warehouse.length - zone.zone_width));
          newY = Math.max(0, Math.min(newY, warehouse.width - zone.zone_height));

          if (showCollisionWarnings) {
            const box: BoundingBox = { x: newX, y: newY, width: zone.zone_width, height: zone.zone_height };
            const collisions = checkZoneCollisions(box, dragState.targetId);
            setCollisionZones(collisions);
          }
        }

        onZoneMove?.(dragState.targetId, { x: newX, y: newY });
        return;
      }

      if (dragState.type === 'move-rack' && dragState.targetId && dragState.initialPosition) {
        const dx = (worldPos.x - dragState.startWorld.x) / SCALE_FACTOR;
        const dy = (worldPos.y - dragState.startWorld.y) / SCALE_FACTOR;

        let newX = dragState.initialPosition.x + dx;
        let newY = dragState.initialPosition.y + dy;

        if (snapToGridEnabled) {
          newX = snapToGrid(newX, gridSize);
          newY = snapToGrid(newY, gridSize);
        }

        const rack = racks.find((r) => r.id === dragState.targetId);
        if (rack) {
          newX = Math.max(0, Math.min(newX, warehouse.length - rack.rack_length));
          newY = Math.max(0, Math.min(newY, warehouse.width - rack.rack_depth));
        }

        onRackMove?.(dragState.targetId, { x: newX, y: newY });
        return;
      }

      if (activeTool === 'select') {
        if (selectedZoneId) {
          const selectedZone = zones.find((z) => z.id === selectedZoneId);
          if (selectedZone) {
            const handle = findResizeHandleAtPosition(screenPos, selectedZone);
            setHoveredHandle(handle);
            if (handle) {
              setCursor(getResizeCursor(handle));
              return;
            }
          }
        }

        const rack = findRackAtPosition(worldPos);
        const zone = findZoneAtPosition(worldPos);

        setHoveredRackId(rack?.id || null);
        setHoveredZoneId(rack ? null : zone?.id || null);

        if (rack || zone) {
          setCursor('pointer');
        } else {
          setCursor('default');
        }
      } else if (activeTool === 'pan') {
        setCursor('grab');
      } else if (activeTool === 'zone' || activeTool === 'rack') {
        setCursor('crosshair');
      } else {
        setCursor('default');
      }

      const warehouseX = worldPos.x / SCALE_FACTOR;
      const warehouseY = worldPos.y / SCALE_FACTOR;
      if (warehouseX >= 0 && warehouseX <= warehouse.length && warehouseY >= 0 && warehouseY <= warehouse.width) {
        setMousePosition({ x: Math.round(warehouseX * 10) / 10, y: Math.round(warehouseY * 10) / 10 });
      } else {
        setMousePosition(null);
      }
    },
    [
      getMousePosition,
      screenToWorld,
      dragState,
      activeTool,
      selectedZoneId,
      zones,
      findZoneAtPosition,
      findRackAtPosition,
      findResizeHandleAtPosition,
      onViewportChange,
      onZoneMove,
      onZoneResize,
      onRackMove,
      snapToGridEnabled,
      gridSize,
      showCollisionWarnings,
      checkZoneCollisions,
      rulerOffset,
      warehouse.length,
      warehouse.width,
    ]
  );

  const handleMouseUp = useCallback(() => {
    if (dragState.type === 'create-zone' && dragState.createStart && onZoneCreate) {
      const startX = Math.min(dragState.createStart.x, dragState.startWorld.x) / SCALE_FACTOR;
      const startY = Math.min(dragState.createStart.y, dragState.startWorld.y) / SCALE_FACTOR;
      const width = Math.abs(dragState.startWorld.x - dragState.createStart.x) / SCALE_FACTOR;
      const height = Math.abs(dragState.startWorld.y - dragState.createStart.y) / SCALE_FACTOR;

      if (width >= MIN_ZONE_SIZE && height >= MIN_ZONE_SIZE) {
        const snappedPos = snapPositionToGrid({ x: startX, y: startY });
        onZoneCreate(snappedPos);
      }
    }

    setCollisionZones([]);

    setDragState({
      type: null,
      startScreen: { x: 0, y: 0 },
      startWorld: { x: 0, y: 0 },
      startViewport: { x: 0, y: 0, scale: 1 },
      targetId: null,
    });

    if (activeTool === 'pan') {
      setCursor('grab');
    } else if (activeTool === 'select') {
      setCursor('default');
    } else if (activeTool === 'zone' || activeTool === 'rack') {
      setCursor('crosshair');
    }
  }, [activeTool, dragState, onZoneCreate, snapPositionToGrid]);

  const handleMouseLeave = useCallback(() => {
    setHoveredZoneId(null);
    setHoveredRackId(null);
    setHoveredHandle(null);
    setMousePosition(null);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const wheelHandler = (e: WheelEvent) => {
      e.preventDefault();

      const rect = canvas.getBoundingClientRect();
      const screenPosX = e.clientX - rect.left;
      const screenPosY = e.clientY - rect.top;

      const currentViewport = viewportRef.current;
      const adjustedScreenPosX = screenPosX - rulerOffset;
      const adjustedScreenPosY = screenPosY - rulerOffset;

      const worldX = (adjustedScreenPosX - currentViewport.x) / currentViewport.scale;
      const worldY = (adjustedScreenPosY - currentViewport.y) / currentViewport.scale;

      const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
      const newScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, currentViewport.scale * zoomFactor));

      const newX = adjustedScreenPosX - worldX * newScale;
      const newY = adjustedScreenPosY - worldY * newScale;

      onViewportChange?.({ x: newX, y: newY, scale: newScale });
    };

    canvas.addEventListener('wheel', wheelHandler, { passive: false });

    return () => {
      canvas.removeEventListener('wheel', wheelHandler);
    };
  }, [canvasRef, onViewportChange, rulerOffset]);

  useEffect(() => {
    if (activeTool === 'pan') {
      setCursor('grab');
    } else if (activeTool === 'select') {
      setCursor('default');
    } else if (activeTool === 'zone' || activeTool === 'rack') {
      setCursor('crosshair');
    }
  }, [activeTool]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden bg-slate-100 dark:bg-slate-800 rounded-lg"
      style={{ minHeight: '400px' }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        style={{ cursor }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onContextMenu={(e) => e.preventDefault()}
      />

      <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-1 z-10">
        <button
          onClick={zoomOut}
          className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
          title="Zoom out (-)"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        </button>
        <span className="px-2 text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[60px] text-center">
          {Math.round(viewport.scale * 100)}%
        </span>
        <button
          onClick={zoomIn}
          className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
          title="Zoom in (+)"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
        <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />
        <button
          onClick={() => fitToContent({ minX: 0, minY: 0, maxX: warehouseWidth, maxY: warehouseHeight })}
          className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
          title="Fit warehouse to view"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
            />
          </svg>
        </button>
        <button
          onClick={resetView}
          className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
          title="Reset view"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>

      <div
        className="absolute bg-white dark:bg-gray-800 rounded-lg shadow-lg px-3 py-2 z-10"
        style={{ left: rulerOffset + 16, top: rulerOffset + 16 }}
      >
        <div className="text-xs font-medium text-gray-600 dark:text-gray-300">
          {warehouse.length} x {warehouse.width} {warehouse.unit}
        </div>
      </div>

      {mousePosition && (
        <div
          className="absolute bg-gray-900/90 dark:bg-gray-700/95 text-white rounded px-2 py-1 z-10 pointer-events-none"
          style={{ left: rulerOffset + 16, bottom: 16 }}
        >
          <div className="text-xs font-mono">
            <span className="text-gray-400">X:</span> {mousePosition.x}{warehouse.unit}
            <span className="mx-2 text-gray-500">|</span>
            <span className="text-gray-400">Y:</span> {mousePosition.y}{warehouse.unit}
          </div>
        </div>
      )}
    </div>
  );
};

function getResizeCursor(handle: ResizeHandle): string {
  const cursors: Record<ResizeHandle, string> = {
    nw: 'nwse-resize',
    se: 'nwse-resize',
    ne: 'nesw-resize',
    sw: 'nesw-resize',
    n: 'ns-resize',
    s: 'ns-resize',
    e: 'ew-resize',
    w: 'ew-resize',
  };
  return cursors[handle];
}

function drawZones(
  ctx: CanvasRenderingContext2D,
  viewport: ViewportState,
  zones: WarehouseZone[],
  selectedId: string | null,
  hoveredId: string | null,
  opacity: number,
  showLabels: boolean,
  isDarkMode: boolean,
  collisionZones: string[],
  showCollisionWarnings: boolean,
  rulerOffset: number
) {
  const dpr = window.devicePixelRatio || 1;
  ctx.save();
  ctx.scale(dpr, dpr);

  for (const zone of zones) {
    const x = zone.x_position * SCALE_FACTOR * viewport.scale + viewport.x + rulerOffset;
    const y = zone.y_position * SCALE_FACTOR * viewport.scale + viewport.y + rulerOffset;
    const width = zone.zone_width * SCALE_FACTOR * viewport.scale;
    const height = zone.zone_height * SCALE_FACTOR * viewport.scale;

    const isSelected = zone.id === selectedId;
    const isHovered = zone.id === hoveredId;
    const hasCollision = showCollisionWarnings && collisionZones.includes(zone.id);

    const alpha = opacity * (isHovered ? 1.2 : 1);
    ctx.fillStyle = hexToRgba(zone.color, Math.min(1, alpha * 0.3));
    ctx.fillRect(x, y, width, height);

    if (hasCollision) {
      ctx.strokeStyle = '#EF4444';
      ctx.lineWidth = 3;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(x, y, width, height);
      ctx.setLineDash([]);
    } else {
      ctx.strokeStyle = isSelected ? '#3B82F6' : isHovered ? zone.color : hexToRgba(zone.color, 0.8);
      ctx.lineWidth = isSelected ? 3 : isHovered ? 2 : 1.5;
      ctx.strokeRect(x, y, width, height);
    }

    if (showLabels && viewport.scale > 0.3) {
      const fontSize = Math.max(10, Math.min(14, 12 * viewport.scale));
      ctx.font = `600 ${fontSize}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const labelText = zone.zone_code;
      const textMetrics = ctx.measureText(labelText);
      const padding = 4;

      const labelX = x + width / 2;
      const labelY = y + height / 2;

      ctx.fillStyle = isDarkMode ? 'rgba(30, 41, 59, 0.9)' : 'rgba(255, 255, 255, 0.9)';
      ctx.beginPath();
      ctx.roundRect(
        labelX - textMetrics.width / 2 - padding,
        labelY - fontSize / 2 - padding,
        textMetrics.width + padding * 2,
        fontSize + padding * 2,
        4
      );
      ctx.fill();

      ctx.fillStyle = hasCollision ? '#EF4444' : zone.color;
      ctx.fillText(labelText, labelX, labelY);
    }
  }

  ctx.restore();
}

function drawRacks(
  ctx: CanvasRenderingContext2D,
  viewport: ViewportState,
  racks: WarehouseRack[],
  zones: WarehouseZone[],
  bins: WarehouseBin[],
  selectedId: string | null,
  hoveredId: string | null,
  showLabels: boolean,
  showBinCounts: boolean,
  isDarkMode: boolean,
  showCollisionWarnings: boolean,
  rulerOffset: number
) {
  const dpr = window.devicePixelRatio || 1;
  ctx.save();
  ctx.scale(dpr, dpr);

  for (const rack of racks) {
    const x = rack.x_position * SCALE_FACTOR * viewport.scale + viewport.x + rulerOffset;
    const y = rack.y_position * SCALE_FACTOR * viewport.scale + viewport.y + rulerOffset;
    const width = rack.rack_length * SCALE_FACTOR * viewport.scale;
    const height = rack.rack_depth * SCALE_FACTOR * viewport.scale;

    const isSelected = rack.id === selectedId;
    const isHovered = rack.id === hoveredId;

    const zone = zones.find((z) => z.id === rack.zone_id);
    const rackColor = zone?.color || (isDarkMode ? '#64748B' : '#94A3B8');

    let isOutsideZone = false;
    if (showCollisionWarnings && zone) {
      const rackBox = getRackBoundingBox(rack);
      const zoneBox = getZoneBoundingBox(zone);
      isOutsideZone =
        rackBox.x < zoneBox.x ||
        rackBox.y < zoneBox.y ||
        rackBox.x + rackBox.width > zoneBox.x + zoneBox.width ||
        rackBox.y + rackBox.height > zoneBox.y + zoneBox.height;
    }

    ctx.fillStyle = isDarkMode ? 'rgba(51, 65, 85, 0.9)' : 'rgba(241, 245, 249, 0.95)';
    ctx.fillRect(x, y, width, height);

    if (isOutsideZone) {
      ctx.strokeStyle = '#F59E0B';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(x, y, width, height);
      ctx.setLineDash([]);
    } else {
      ctx.strokeStyle = isSelected ? '#3B82F6' : isHovered ? rackColor : isDarkMode ? '#475569' : '#CBD5E1';
      ctx.lineWidth = isSelected ? 2 : isHovered ? 1.5 : 1;
      ctx.strokeRect(x, y, width, height);
    }

    if (viewport.scale > 0.5) {
      const bayWidth = width / rack.bays;
      ctx.strokeStyle = isDarkMode ? 'rgba(100, 116, 139, 0.3)' : 'rgba(148, 163, 184, 0.4)';
      ctx.lineWidth = 0.5;

      for (let i = 1; i < rack.bays; i++) {
        ctx.beginPath();
        ctx.moveTo(x + i * bayWidth, y);
        ctx.lineTo(x + i * bayWidth, y + height);
        ctx.stroke();
      }
    }

    if (zone) {
      const indicatorSize = Math.max(6, 8 * viewport.scale);
      ctx.fillStyle = zone.color;
      ctx.beginPath();
      ctx.arc(x + width - indicatorSize, y + indicatorSize, indicatorSize / 2, 0, Math.PI * 2);
      ctx.fill();
    }

    if (showLabels && viewport.scale > 0.4) {
      const fontSize = Math.max(8, Math.min(11, 10 * viewport.scale));
      ctx.font = `500 ${fontSize}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      ctx.fillStyle = isOutsideZone ? '#F59E0B' : isDarkMode ? '#E2E8F0' : '#334155';
      ctx.fillText(rack.rack_code, x + width / 2, y + height / 2);
    }

    if (showBinCounts && viewport.scale > 0.6) {
      const rackBins = bins.filter((b) => b.rack_id === rack.id);
      const totalBins = rack.levels * rack.bays;
      const usedBins = rackBins.length;
      const countText = `${usedBins}/${totalBins}`;

      const fontSize = Math.max(7, Math.min(9, 8 * viewport.scale));
      ctx.font = `400 ${fontSize}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillStyle = isDarkMode ? '#94A3B8' : '#64748B';
      ctx.fillText(countText, x + width - 2, y + height - 2);
    }
  }

  ctx.restore();
}

function drawSelectionHandles(
  ctx: CanvasRenderingContext2D,
  viewport: ViewportState,
  worldX: number,
  worldY: number,
  worldWidth: number,
  worldHeight: number,
  isDarkMode: boolean,
  hoveredHandle: ResizeHandle | null,
  rulerOffset: number
) {
  const dpr = window.devicePixelRatio || 1;
  ctx.save();
  ctx.scale(dpr, dpr);

  const x = worldX * viewport.scale + viewport.x + rulerOffset;
  const y = worldY * viewport.scale + viewport.y + rulerOffset;
  const width = worldWidth * viewport.scale;
  const height = worldHeight * viewport.scale;

  const handleSize = 8;
  const handles: { handle: ResizeHandle; x: number; y: number }[] = [
    { handle: 'nw', x: x - handleSize / 2, y: y - handleSize / 2 },
    { handle: 'ne', x: x + width - handleSize / 2, y: y - handleSize / 2 },
    { handle: 'sw', x: x - handleSize / 2, y: y + height - handleSize / 2 },
    { handle: 'se', x: x + width - handleSize / 2, y: y + height - handleSize / 2 },
    { handle: 'n', x: x + width / 2 - handleSize / 2, y: y - handleSize / 2 },
    { handle: 's', x: x + width / 2 - handleSize / 2, y: y + height - handleSize / 2 },
    { handle: 'w', x: x - handleSize / 2, y: y + height / 2 - handleSize / 2 },
    { handle: 'e', x: x + width - handleSize / 2, y: y + height / 2 - handleSize / 2 },
  ];

  ctx.lineWidth = 2;

  for (const h of handles) {
    const isHovered = h.handle === hoveredHandle;
    ctx.fillStyle = isHovered ? '#3B82F6' : '#FFFFFF';
    ctx.strokeStyle = '#3B82F6';
    ctx.fillRect(h.x, h.y, handleSize, handleSize);
    ctx.strokeRect(h.x, h.y, handleSize, handleSize);
  }

  ctx.restore();
}

function drawRackSelection(
  ctx: CanvasRenderingContext2D,
  viewport: ViewportState,
  rack: WarehouseRack,
  isDarkMode: boolean,
  rulerOffset: number
) {
  const dpr = window.devicePixelRatio || 1;
  ctx.save();
  ctx.scale(dpr, dpr);

  const x = rack.x_position * SCALE_FACTOR * viewport.scale + viewport.x + rulerOffset;
  const y = rack.y_position * SCALE_FACTOR * viewport.scale + viewport.y + rulerOffset;
  const width = rack.rack_length * SCALE_FACTOR * viewport.scale;
  const height = rack.rack_depth * SCALE_FACTOR * viewport.scale;

  ctx.strokeStyle = '#3B82F6';
  ctx.lineWidth = 2;
  ctx.setLineDash([4, 4]);
  ctx.strokeRect(x - 2, y - 2, width + 4, height + 4);
  ctx.setLineDash([]);

  ctx.restore();
}

function drawZoneCreationPreview(
  ctx: CanvasRenderingContext2D,
  viewport: ViewportState,
  start: Position,
  end: Position,
  isDarkMode: boolean,
  rulerOffset: number
) {
  const dpr = window.devicePixelRatio || 1;
  ctx.save();
  ctx.scale(dpr, dpr);

  const startX = Math.min(start.x, end.x) * viewport.scale + viewport.x + rulerOffset;
  const startY = Math.min(start.y, end.y) * viewport.scale + viewport.y + rulerOffset;
  const width = Math.abs(end.x - start.x) * viewport.scale;
  const height = Math.abs(end.y - start.y) * viewport.scale;

  ctx.fillStyle = isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)';
  ctx.fillRect(startX, startY, width, height);

  ctx.strokeStyle = '#3B82F6';
  ctx.lineWidth = 2;
  ctx.setLineDash([6, 4]);
  ctx.strokeRect(startX, startY, width, height);
  ctx.setLineDash([]);

  ctx.restore();
}

function drawRulers(
  ctx: CanvasRenderingContext2D,
  viewport: ViewportState,
  canvasWidth: number,
  canvasHeight: number,
  unit: string,
  isDarkMode: boolean
) {
  const dpr = window.devicePixelRatio || 1;
  ctx.save();
  ctx.scale(dpr, dpr);

  const bgColor = isDarkMode ? '#1e293b' : '#f8fafc';
  const borderColor = isDarkMode ? '#334155' : '#e2e8f0';
  const textColor = isDarkMode ? '#94a3b8' : '#64748b';
  const tickColor = isDarkMode ? '#475569' : '#cbd5e1';

  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, canvasWidth, RULER_SIZE);
  ctx.fillRect(0, 0, RULER_SIZE, canvasHeight);

  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, RULER_SIZE, RULER_SIZE);

  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(RULER_SIZE, 0);
  ctx.lineTo(RULER_SIZE, canvasHeight);
  ctx.moveTo(0, RULER_SIZE);
  ctx.lineTo(canvasWidth, RULER_SIZE);
  ctx.stroke();

  const pixelsPerUnit = SCALE_FACTOR * viewport.scale;
  let tickInterval = 1;

  if (pixelsPerUnit < 5) tickInterval = 50;
  else if (pixelsPerUnit < 10) tickInterval = 20;
  else if (pixelsPerUnit < 20) tickInterval = 10;
  else if (pixelsPerUnit < 50) tickInterval = 5;
  else if (pixelsPerUnit < 100) tickInterval = 2;

  const startX = Math.floor(-viewport.x / pixelsPerUnit / tickInterval) * tickInterval;
  const endX = Math.ceil((canvasWidth - viewport.x - RULER_SIZE) / pixelsPerUnit / tickInterval) * tickInterval;

  ctx.font = '10px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillStyle = textColor;
  ctx.strokeStyle = tickColor;

  for (let x = startX; x <= endX; x += tickInterval) {
    const screenX = x * pixelsPerUnit + viewport.x + RULER_SIZE;
    if (screenX < RULER_SIZE || screenX > canvasWidth) continue;

    const isMajor = x % (tickInterval * 5) === 0;
    const tickHeight = isMajor ? 10 : 5;

    ctx.beginPath();
    ctx.moveTo(screenX, RULER_SIZE - tickHeight);
    ctx.lineTo(screenX, RULER_SIZE);
    ctx.stroke();

    if (isMajor && viewport.scale > 0.3) {
      ctx.fillText(String(x), screenX, RULER_SIZE - tickHeight - 2);
    }
  }

  const startY = Math.floor(-viewport.y / pixelsPerUnit / tickInterval) * tickInterval;
  const endY = Math.ceil((canvasHeight - viewport.y - RULER_SIZE) / pixelsPerUnit / tickInterval) * tickInterval;

  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';

  for (let y = startY; y <= endY; y += tickInterval) {
    const screenY = y * pixelsPerUnit + viewport.y + RULER_SIZE;
    if (screenY < RULER_SIZE || screenY > canvasHeight) continue;

    const isMajor = y % (tickInterval * 5) === 0;
    const tickWidth = isMajor ? 10 : 5;

    ctx.beginPath();
    ctx.moveTo(RULER_SIZE - tickWidth, screenY);
    ctx.lineTo(RULER_SIZE, screenY);
    ctx.stroke();

    if (isMajor && viewport.scale > 0.3) {
      ctx.save();
      ctx.translate(RULER_SIZE - tickWidth - 4, screenY);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.fillText(String(y), 0, 0);
      ctx.restore();
    }
  }

  ctx.fillStyle = isDarkMode ? '#64748b' : '#94a3b8';
  ctx.font = '8px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(unit, RULER_SIZE / 2, RULER_SIZE / 2);

  ctx.restore();
}

function hexToRgba(hex: string, alpha: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return `rgba(0, 0, 0, ${alpha})`;

  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
